import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { IoArrowBack } from 'react-icons/io5';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape, useIntl } from '../../util/reactIntl';

// Use your existing components if you have them
import { NamedLink } from '../../components';
import { selectCurrentUser } from '../../ducks/user.duck';
import { isCorporateUser as isCorporateUserType } from '../../util/dataExtractors';

import css from './AccountSettingsLayout.module.css';
import Icons from '../Icons/Icons';

import Drawer from 'react-modern-drawer';

import '../../styles/drawer.css';

const SIDEBAR_ITEMS = [
  {
    key: 'contact',
    label: <FormattedMessage id="AccountSettingsLayout.contactLabel" />,
    page: 'ContactDetailsPage',
  },
  {
    key: 'password',
    label: <FormattedMessage id="AccountSettingsLayout.passwordLabel" />,
    page: 'PasswordChangePage',
  },
  {
    key: 'subscription',
    label: <FormattedMessage id="AccountSettingsLayout.subscriptionLabel" />,
    page: 'SubscriptionPage',
  },
  {
    key: 'invoices',
    label: <FormattedMessage id="AccountSettingsLayout.invoicesLabel" />,
    page: 'MyInvoicesPage',
  },
  {
    key: 'personal',
    label: <FormattedMessage id="AccountSettingsLayout.personalLabel" />,
    page: 'PersonalSettingsPage',
  },
  {
    key: 'language',
    label: <FormattedMessage id="AccountSettingsLayout.languageLabel" />,
    page: 'LanguagePreferencesPage',
  },
  {
    key: 'payment',
    label: <FormattedMessage id="AccountSettingsLayout.paymentLabel" />,
    page: 'PaymentMethodsPage',
  },
];

const AccountSettingsLayoutComponent = props => {
  const { title, children, className, isActive, viewport } = props;

  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const currentUser = useSelector(selectCurrentUser);
  const isCorporateUser = isCorporateUserType(currentUser);

  const activeFn = isActive || ((pathname, item) => pathname.toLowerCase().includes(item.key));
  const isMobileLayout = viewport.width > 0 && viewport.width < 768;

  const [isDrawerOpen, setOpenDrawer] = useState(false);

  const effectiveTitle = title || intl.formatMessage({ id: 'AccountSettingsLayout.title' });
  const sidebarItems = isCorporateUser
    ? SIDEBAR_ITEMS.filter(item => item.key !== 'subscription' && item.key !== 'invoices')
    : SIDEBAR_ITEMS;

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.sidebar}>
        <div className={css.sidebarHead}>
          {isMobileLayout ? (
            <button className={css.backButton} onClick={() => setOpenDrawer(!isDrawerOpen)}>
              <Icons name="menu" />
            </button>
          ) : (
            <button className={css.backButton} onClick={() => history.goBack()}>
              <IoArrowBack size={16} />
              <span className={css.backLabel}>
                {intl.formatMessage({ id: 'AccountSettingsLayout.backLabel' })}
              </span>
            </button>
          )}
          <div className={css.sidebarTitle}>{effectiveTitle}</div>
        </div>
        {isMobileLayout ? (
          <Drawer
            open={isDrawerOpen}
            onClose={() => {
              setOpenDrawer(false);
            }}
            direction="bottom"
            className={css.filterDrawer}
            lockBackgroundScroll
            overlayOpacity="45%"
            overlayColor="#000000"
          >
            <div className={css.drawerHead}>{effectiveTitle}</div>
            <nav className={css.nav}>
              {sidebarItems.map(item => {
                const active = activeFn(location.pathname, item);
                return (
                  <NamedLink
                    key={item.key}
                    name={item.page}
                    className={classNames(css.navItem, active ? css.active : null)}
                  >
                    {item.label}
                  </NamedLink>
                );
              })}
            </nav>
          </Drawer>
        ) : (
          <nav className={css.nav}>
            {sidebarItems.map(item => {
              const active = activeFn(location.pathname, item);
              return (
                <NamedLink
                  key={item.key}
                  name={item.page}
                  className={classNames(css.navItem, active ? css.active : null)}
                >
                  {item.label}
                </NamedLink>
              );
            })}
          </nav>
        )}
      </div>

      <div className={css.content}>{children}</div>
    </div>
  );
};

AccountSettingsLayoutComponent.defaultProps = {
  title: null,
  className: null,
  isActive: null,
};

AccountSettingsLayoutComponent.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  intl: intlShape.isRequired,

  /**
   * Optional override for “active” logic:
   * (pathname: string, item: {key,label,page}) => boolean
   */
  isActive: PropTypes.func,
};

const AccountSettingsLayout = compose(injectIntl)(AccountSettingsLayoutComponent);

export default AccountSettingsLayout;
